import React from "react";
import { Flex, Box, AspectRatio, SimpleGrid, Text } from "@chakra-ui/react";
import { DecorativeBackgroundImage } from "components/images";
import { TextTitle } from "ui/text";
import { remCalc } from "theme/utils";
import { Button } from "ui/button";
import { desktopOnlyDisplay, mobileOnlyDisplay } from "utils";
import { VerticalFeatureCard, type VerticalFeatureCardProps } from "./vertical-feature-card";

/**
 * `HorizontalFeatureCard` is a dark themed card featuring a media element positioned to the left of text content.
 * On mobile screens, `VerticalFeatureCard` is displayed instead.
 */
export function HorizontalFeatureCard({
  bgColor,
  title,
  description,
  href,
  buttonText,
  image,
  imageRatio,
  ...props
}: VerticalFeatureCardProps) {
  return (
    <Box>
      <SimpleGrid
        display={desktopOnlyDisplay("grid")}
        alignItems="start"
        columns={2}
        bg={bgColor}
        borderRadius="bigger"
        pos="relative"
        w="100%"
        overflow="hidden"
        {...props}
      >
        <AspectRatio ratio={imageRatio} pos="relative" w="100%" h="auto">
          <DecorativeBackgroundImage src={image} style={{ objectFit: "contain", objectPosition: "left center" }} />
        </AspectRatio>

        <Flex
          align="center"
          alignSelf="center"
          flexDir="column"
          gap="smaller"
          pos="relative"
          p={{ base: "bigger", lg: remCalc(72) }}
        >
          <TextTitle
            size="xs"
            theme="dark"
            titleAs="h3"
            title={
              <Text as="span" fontSize="brand.xl">
                {title}
              </Text>
            }
            description={description}
            actions={
              <Button hasRightChevron href={href}>
                {buttonText}
              </Button>
            }
            maxW="360px"
          />
        </Flex>
      </SimpleGrid>

      <VerticalFeatureCard
        display={mobileOnlyDisplay("flex")}
        bgColor={bgColor}
        title={title}
        description={description}
        image={image}
        imageRatio={imageRatio}
        href={href}
        buttonText={buttonText}
        {...props}
      />
    </Box>
  );
}
